import React from 'react';
import Tooltip from '@mui/material/Tooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import warningIcon from 'Assets/Icons/warning.svg';
import acuteIcon from 'Assets/Icons/acute.svg';
import { DataGrid } from 'v3/Components/DataGrid/DataGrid';
import { DateRangePickerFilterFilter } from 'v3/Components/DataGrid/ColumnFilters/DateRangePickerFilter';
import { MultiSelectFilter } from 'v3/Components/DataGrid/ColumnFilters/MultiSelectFilter';
import { TextFieldFilter } from 'v3/Components/DataGrid/ColumnFilters/TextFieldFilter';
import { StatusPill } from 'v3/Components/Shared/StatusPill';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import { Permission } from 'Models/Templates/Permission/Permission';
import { buildEndpoint, buildRoute, defaultDataTransform, useApiWorker, useDataFetcher, useHasPermissions, formatShortDateTime, ExpeditedPriority } from 'Utilities';
import { useEnums, useGlobalContext } from 'Context';
import { useHistory } from 'react-router-dom';
import { AddEditSubRoute, ROUTES } from 'Navigation';
import NewReferralButton from 'v3/Components/Shared/NewReferralButton';
import './DraftReferrals.scss';

interface DropdownOptions {
  lobs: { label: string; value: string }[];
  services: { label: string; value: string }[];
  subServices: { label: string; value: string }[];
  recentUsers: { label: string; value: string }[];
}

interface ActionHandlers {
  onDelete: (id: string) => Promise<void>;
  canDelete: boolean;
}

const buildColumns = (opts: DropdownOptions, actions: ActionHandlers): any[] => [
  {
    field: 'referralDate',
    headerName: 'Referral Date & Time',
    width: 200,
    widthOnFilter: 260,
    renderCell: (params: any) => {
      const formatted = params.value ? formatShortDateTime(params.value) : '';
      if (!params.value) return <span>{formatted}</span>;

      const MS_PER_DAY = 1000 * 60 * 60 * 24;
      const daysInactive = Math.floor(
        (Date.now() - new Date(params.value).getTime()) / MS_PER_DAY
      );
      const showWarning = daysInactive >= 5 && daysInactive < 30;

      if (!showWarning) return <span>{formatted}</span>;

      const xLabel = daysInactive === 1 ? '1 day' : `${daysInactive} days`;
      return (
        <div className="draft-referrals-date-cell">
          <span>{formatted}</span>
          <Tooltip title={`This item hasn't been active for ${xLabel}. Take action if you want to keep it.`}>
            <img src={warningIcon} alt="Inactive draft warning" className="draft-referrals-warning-icon" />
          </Tooltip>
        </div>
      );
    },
    renderHeaderFilter: (params: any) => <DateRangePickerFilterFilter {...params} />,
  },
  {
    field: 'lob',
    headerName: 'Line Of Business',
    width: 260,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => <MultiSelectFilter {...params} optionItems={opts.lobs} />,
  },
  {
    field: 'name',
    headerName: 'Claimant Name',
    width: 200,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'claimNumber',
    headerName: 'Claim/Client #',
    width: 160,
    renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
  },
  {
    field: 'recentUser',
    headerName: 'Recent User',
    width: 180,
    sortable: false,
    renderHeaderFilter: (params: any) => <MultiSelectFilter {...params} optionItems={opts.recentUsers} />,
  },
  {
    field: 'statusName',
    headerName: 'Status',
    width: 180,
    renderCell: (params: any) =>
      params.value ? <StatusPill status={params.value} /> : null,
    filterable: false,
  },
  {
    field: 'dueDate',
    headerName: 'Due Date & Time',
    width: 200,
    widthOnFilter: 260,
    renderCell: (params: any) => {
      const formatted = params.value ? formatShortDateTime(params.value) : '';
      const isExpedited = params.row?.tatPriority === ExpeditedPriority;
      if (!isExpedited) return <span>{formatted}</span>;

      return (
        <div className="draft-referrals-date-cell">
          <span>{formatted}</span>
          <Tooltip title="Expedited" placement="bottom">
            <img src={acuteIcon} alt="Expedited" className="draft-referrals-expedited-icon" />
          </Tooltip>
        </div>
      );
    },
    renderHeaderFilter: (params: any) => <DateRangePickerFilterFilter {...params} />,
  },
  {
    field: 'service',
    headerName: 'Service',
    width: 200,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => <MultiSelectFilter {...params} optionItems={opts.services} />,
  },
  {
    field: 'subService',
    headerName: 'Sub Service',
    width: 220,
    type: 'singleSelect',
    renderHeaderFilter: (params: any) => <MultiSelectFilter {...params} optionItems={opts.subServices} />,
  },
  {
    field: 'actions',
    headerName: ' ',
    width: 60,
    sortable: false,
    filterable: false,
    hideable: false,
    disableColumnMenu: true,
    disableReorder: true,
    pinnable: false,
    renderCell: (params: any) =>
      actions.canDelete ? (
        <div className="draft-referrals-action-cell">
          <button
            className="draft-referrals-action-btn"
            onClick={(e) => {
              e.stopPropagation();
              actions.onDelete(params.row.id);
            }}
            aria-label="Delete draft"
          >
            <DeleteIcon />
          </button>
        </div>
      ) : null,
  },
];

const useDraftReferralsDropdownOptions = (): DropdownOptions => {
  const enums: any = useEnums();
  const filterOptionsEndpoint = ENDPOINTS.draftreferrals.endpointString('FilterOptions');

  const [filterOptions] = useDataFetcher<{
    services: { id: string; name: string }[];
    subServices: { id: string; name: string }[];
    recentUsers: string[];
  }>(
    buildEndpoint(filterOptionsEndpoint),
    { services: [], subServices: [], recentUsers: [] },
    defaultDataTransform,
    undefined,
    true,
    '3.0',
  );

  return {
    lobs: Object.values(enums?.lineOfBusiness ?? {}).map((e: any) => ({ label: e.displayName ?? '', value: String(e.value ?? '') })),
    services: (filterOptions?.services ?? []).map((s) => ({ label: s.name, value: s.id })),
    subServices: (filterOptions?.subServices ?? []).map((s) => ({ label: s.name, value: s.id })),
    recentUsers: (filterOptions?.recentUsers ?? []).map((s) => ({ label: s, value: s })),
  };
};

interface DraftReferralsProps {
  header?: boolean;
  id?: string;
  clientId?: string | null;
  disableUrlSync?: boolean;
}

export const DraftReferrals: React.FC<DraftReferralsProps> = ({
  header = true,
  clientId = null,
  disableUrlSync = false,
}) => {
  const history = useHistory();
  const globalContext = useGlobalContext();
  const isInternal = globalContext[0]?.currentUser?.isInternal ?? false;
  const API = useApiWorker('3.0');
  const hasPermissions = useHasPermissions();
  const canDelete = hasPermissions([Permission.DeleteReferralsRequestDraft]) ?? false;
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  const readEndpoint = buildEndpoint(ENDPOINTS.draftreferrals.read);
  const exportEndpoint = buildEndpoint(ENDPOINTS.draftreferrals.endpointString('ExportToCsv'));
  const dropdownOptions = useDraftReferralsDropdownOptions();

  const handleDeleteDraft = React.useCallback(async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this?')) return;
    await API.delete(`${ENDPOINTS.referralsRequestsDraft.base}/${id}`);
    setRefreshKey((k) => k + 1);
  }, [API]);

  const handleBulkDelete = React.useCallback(async () => {
    try {
      setDeleteLoading(true);
      await Promise.all(
        selectedIds.map((id) =>
          API.delete(`${ENDPOINTS.referralsRequestsDraft.base}/${id}`)
        )
      );
      setSelectedIds([]);
      setIsDeleteModalOpen(false);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      console.error('Error deleting selected drafts:', err);
    } finally {
      setDeleteLoading(false);
    }
  }, [API, selectedIds]);

  const columns = React.useMemo(
    () => buildColumns(dropdownOptions, { onDelete: handleDeleteDraft, canDelete }),
    [dropdownOptions, handleDeleteDraft, canDelete]
  );

  const additionalSearchParams = React.useMemo(
    () => ({ _refresh: refreshKey, ...(clientId ? { clientId } : {}) }),
    [refreshKey, clientId]
  );

  const legacyParams = React.useMemo(() => ({
    readEndpoint,
    apiVersion: '3.0',
  }), [readEndpoint]);

  const handleRowClick = (params: any, event: any) => {
    const target = event?.target as HTMLElement;
    if (target?.closest('[data-field="__check__"]')) return;
    const id = params.row?.id;
    const lob = params.row?.lineOfBusiness?.value ?? '';
    const clientId = params.row?.clientId ?? '';
    const referrerId = params.row?.referralSourceId;
    if (id) {
      history.push(
        buildRoute(
          ROUTES.ADD_EDIT,
          AddEditSubRoute.ReferralsRequest,
          id +
            '?lob=' + lob +
            '&draft=1' +
            '&clientId=' + clientId +
            (referrerId ? '&referrerId=' + referrerId : '')
        )
      );
    }
  };

  const selectionBanner = selectedIds.length > 0 ? (
    <div className="draft-referrals-selection-banner">
      <span className="draft-referrals-selection-count">
        You've selected {selectedIds.length} item{selectedIds.length === 1 ? '' : 's'}
      </span>
      <button
        className="draft-referrals-delete-selected-btn"
        onClick={() => setIsDeleteModalOpen(true)}
        disabled={deleteLoading}
      >
        Delete Selected
      </button>
    </div>
  ) : null;

  return (
    <>
      <div className="draftReferrals">
        {header && (
          <div className="main-grid-header">
            <h1>Referral Drafts</h1>
            <NewReferralButton />
          </div>
        )}
        <div className="draft-referrals-content">
          <DataGrid
            onRowClick={handleRowClick}
            title="Referral Drafts"
            gridId="draftReferralsGrid"
            enableUrlSync={!disableUrlSync}
            legacyParams={legacyParams}
            additionalSearchParams={additionalSearchParams}
            columns={columns}
            exportEndpoint={exportEndpoint}
            exportFileName="DraftReferrals"
            defaultShowFilters={isInternal}
            checkboxSelection={canDelete}
            rowSelectionModel={selectedIds}
            onRowSelectionModelChange={setSelectedIds}
            headerActions={selectionBanner}
            enableAutoRefresh
            toolbarPermissions={{ showColumns: header }}
          />
        </div>
      </div>

      {isDeleteModalOpen && (
        <div className="draft-referrals-modal-overlay">
          <div
            className="draft-referrals-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="draft-referrals-modal-title">
              Are you sure you want to discard {selectedIds.length}{' '}
              {selectedIds.length === 1 ? 'draft' : 'drafts'}?
            </p>
            <p className="draft-referrals-modal-body">
              You'll lose all the information on these referrals.
            </p>
            <div className="draft-referrals-modal-actions">
              <button
                className="draft-referrals-modal-btn draft-referrals-modal-btn--secondary"
                onClick={() => setIsDeleteModalOpen(false)}
                disabled={deleteLoading}
              >
                Keep Drafts
              </button>
              <button
                className="draft-referrals-modal-btn draft-referrals-modal-btn--danger"
                onClick={handleBulkDelete}
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Discarding...' : 'Discard Drafts'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
